import { FC, useContext, useMemo } from 'react'
import { ThemeProvider } from '@mui/material'
import { createTheme, darken, lighten } from '@mui/material/styles'
import { AppConfigContext } from '../contexts/app-config.context'
import type {} from '@mui/x-data-grid/themeAugmentation'
import { Theme } from '@mui/material'

declare module '@mui/material/styles' {
  interface Palette {
    elevated: Palette['primary']
  }

  interface PaletteOptions {
    elevated?: PaletteOptions['primary']
  }
}

export const create = (isLightMode: boolean): Theme =>
  createTheme({
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isLightMode
              ? '0px -1px 2px 2px rgba(0, 0, 0, 0.3)'
              : 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            background:
              'rgba(255, 255, 255, ' + (isLightMode ? 0.2 : 0.06) + ')',
            backdropFilter: 'blur(20px)',
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            height: '100%',
            width: '100%',
          },
          cell: {
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
          },
          columnHeader: {
            '& div': {
              fontWeight: 600,
            },
            ':focus': {
              outline: 'none',
            },
            background: isLightMode
              ? 'rgba(0, 0, 0, 0.06)'
              : 'rgba(255, 255, 255, 0.06)',
          },
          footerContainer: {
            '& p': {
              marginBottom: 0,
            },
            background: isLightMode
              ? 'rgba(0, 0, 0, 0.06)'
              : 'rgba(255, 255, 255, 0.06)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: isLightMode ? '#ffffffee' : '#1d1c2699',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: isLightMode
            ? {
                background: 'rgba(255, 255, 255, 0.35)',
              }
            : {
                background: 'rgba(51, 49, 62, 0.16)',
                textShadow: '0px 1px 7px rgba(0,0,0,0.2)',
                '& .MuiButtonBase-root': {
                  textShadow: 'inherit',
                },
              },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            outline: 'none',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            '&::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '&::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            outline: 'none !important',
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            outline: 'none',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            outline: 'none',
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            '& > div:nth-of-type(3)': {
              background: isLightMode ? '#ffffffee' : '#1d1c2699',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(20px)',
              '& > div': {},
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            '& div': { display: 'flex', justifyContent: 'center' },
          },
        },
      },
    },
    palette: {
      mode: isLightMode ? 'light' : 'dark',
      background: {
        default: isLightMode ? '#ffffff' : '#100c2a',
      },
      elevated: {
        main: isLightMode ? darken('#fff', 0.02) : lighten('#100c2a', 0.15),
        light: isLightMode ? darken('#fff', 0.13) : lighten('#100c2a', 0.25),
        dark: isLightMode ? darken('#fff', 0.3) : lighten('#100c2a', 0.45),
        contrastText: isLightMode
          ? darken('#fff', 0.65)
          : lighten('#100c2a', 0.75),
      },
    },
  })

interface IProps {
  children: JSX.Element
}

export const MUIThemeProvider: FC<IProps> = ({ children }) => {
  const { isLightMode } = useContext(AppConfigContext)

  const theme = useMemo<Theme>(() => create(isLightMode), [isLightMode])

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}
